// Importing everything we need to talk to the supabase tables
import { supabase } from "../lib/supabase.js"; // Importing the supabase client

// Beta distribution defaults by proficiency level (easy to adjust)

// Gaussian (Normal) prior defaults for Rasch onboarding by self-assessment
// sd = 3 => var = 9. Pre-beginner should skip test and set starting_word_rank = 0.
export const GAUSSIAN_DEFAULTS_BY_LEVEL = {
  beginner: { mean: -3, var: 9 },
  intermediate: { mean: 0, var: 9 },
  advanced: { mean: 3, var: 9 },
  // "pre-beginner": handled by skipping test and setting starting_word_rank = 0
};

// Normalization for default_ranking used in Rasch onboarding
export const RANK_NORMALIZATION = Object.freeze({
  center: 2500,
  scale: 500,
});

// Normalize(δ_raw)   = (δ_raw - 2500) / 500
export function normalizeRanking(
  raw,
  center = RANK_NORMALIZATION.center,
  scale = RANK_NORMALIZATION.scale
) {
  const r = Number(raw);
  return (r - center) / scale;
}

// DeNormalize(δ_norm) = (δ_norm * 500) + 2500
export function denormalizeRanking(
  norm,
  center = RANK_NORMALIZATION.center,
  scale = RANK_NORMALIZATION.scale
) {
  const n = Number(norm);
  return n * scale + center;
}

// Probe eligibility: only every 5th card id
export function isProbeEligibleId(id) {
  return Number(id) % 5 === 0;
}

/**
 * Update FSRS fields and schedule user_cards after onboarding test completion.
 *
 * Applies to all user_cards matching:
 * - user_cards.language = user_preferences.active_language
 * - user_cards.default_ranking <= starting_word_rank[active_language]
 *
 * For each matched card:
 * - difficulty = 1
 * - stability = 25
 * - state = 3
 * - scheduled_days = floor(index_in_sorted_list / 10) + 1
 * - due = today + scheduled_days (timestamptz)
 *
 * Sorting:
 * - Sort by default_ranking DESC (highest frequency first)
 * - Use 0-based index in that sorted list to compute scheduled_days
 */
export async function updateFSRSAfterOnboarding() {
  try {
    // Resolve current user
    const { data: auth } = await supabase.auth.getUser();
    const uid = auth?.user?.id || null;
    if (!uid) {
      return { success: false, error: "Missing user id" };
    }

    // Fetch active language and starting rank
    const { data: pref, error: prefErr } = await supabase
      .from("user_preferences")
      .select("active_language, starting_word_rank")
      .eq("user_id", uid)
      .maybeSingle();

    if (prefErr) {
      console.error("[updateFSRSAfterOnboarding] preferences read error", {
        message: prefErr?.message || prefErr,
        uid,
      });
      return {
        success: false,
        error: prefErr?.message || "Failed to read preferences",
      };
    }

    const lang = pref?.active_language || null;
    const startRank =
      pref?.starting_word_rank && lang in pref.starting_word_rank
        ? pref.starting_word_rank[lang]
        : null;

    console.log("[updateFSRSAfterOnboarding] resolved prefs", {
      lang,
      startRank,
      hasStartingWordRank: !!pref?.starting_word_rank,
    });

    if (!lang || typeof startRank !== "number") {
      return {
        success: false,
        error:
          "Missing active language or starting_word_rank for this language",
      };
    }

    // Fetch all matching user_cards: default_ranking <= startRank for active language
    const { data: cards, error: cardsErr } = await supabase
      .from("user_cards")
      .select("id, default_ranking")
      .eq("user_id", uid)
      .eq("language", lang)
      .lte("default_ranking", startRank)
      .order("default_ranking", { ascending: false });

    if (cardsErr) {
      console.error("[updateFSRSAfterOnboarding] user_cards fetch error", {
        message: cardsErr?.message || cardsErr,
        uid,
        lang,
        startRank,
      });
      return {
        success: false,
        error: cardsErr?.message || "Failed to fetch user_cards",
      };
    }

    const list = Array.isArray(cards) ? cards : [];
    console.log("[updateFSRSAfterOnboarding] matched user_cards", {
      count: list.length,
      topSample: list.slice(0, 5),
      bottomSample: list.slice(-5),
    });
    if (list.length === 0) {
      return { success: true, updated: 0, total: 0, language: lang };
    }

    const today = new Date();
    const addDays = (base, days) => {
      const d = new Date(base);
      d.setDate(d.getDate() + days);
      return d;
    };

    let updated = 0;

    // Iterate in sorted order and update each card
    for (let i = 0; i < list.length; i++) {
      const row = list[i];
      const scheduled_days = Math.floor(i / 10) + 1;
      const due = addDays(today, scheduled_days).toISOString();

      const patch = {
        difficulty: 1,
        stability: 25,
        state: 3,
        scheduled_days,
        due,
      };

      if (i < 5 || i % 50 === 0 || i === list.length - 1) {
        console.log("[updateFSRSAfterOnboarding] updating card", {
          idx: i,
          id: row.id,
          default_ranking: row.default_ranking,
          scheduled_days,
          due,
          patch,
        });
      }

      const { error: updErr } = await supabase
        .from("user_cards")
        .update(patch)
        .eq("user_id", uid)
        .eq("id", row.id);

      if (updErr) {
        // continue with other rows; log for debugging
        // eslint-disable-next-line no-console
        console.error("updateFSRSAfterOnboarding: failed to update user_card", {
          id: row.id,
          error: updErr?.message || updErr,
        });
      } else {
        updated += 1;
      }
    }

    console.log("[updateFSRSAfterOnboarding] complete", {
      language: lang,
      updated,
      total: list.length,
    });
    return { success: true, updated, total: list.length, language: lang };
  } catch (e) {
    console.error("[updateFSRSAfterOnboarding] unexpected error", {
      message: e?.message || String(e),
      stack: e?.stack || null,
    });
    return { success: false, error: e?.message || String(e) };
  }
}

// Update or insert beta_distribution for current user for a target language
// Shape stored: { [languageCode]: { a: number, b: number }, ... }
export function updateUserBetaDistribution() {
  return {
    success: false,
    error:
      "beta_distribution is deprecated and not available; use language_ability_distribution instead",
  };
}

// BASIC DATA
// The columns for the flashcards table are:
// id: integer (primary key)
// default_ranking: integer (default word importance ranking of the flashcard)
// language: text (language of the flashcard)
// language_word: text (word in the language of the flashcard)
// language_sentence: text (sentence in the language of the flashcard that contains the flashcard word)
// lanuge_word_mappings: jsonb (json object with a mapping of 'other_language' to ' word translation')
// language_sentence_mappings: jsonb (json object with a mapping of 'other_language' to 'sentence translation')
// language_word_audio: text (url to the audio of the word in the language of the flashcard)
// language_sentence_audio: text (url to the audio of the sentence in the language of the flashcard)

// The columns for the user_flashcards table are:
// id: integer (primary key that matches the id of the flashcard)
// user_id: uuid (foreign key to the users table)
// due: Date;             // Date when the card is next due for review
// stability: number;     // A measure of how well the information is retained
// difficulty: number;    // Reflects the inherent difficulty of the card content
// scheduled_days: number;// The interval of time in days between this review and the next one
// reps: number;          // Total number of times the card has been reviewed
// lapses: number;        // Times the card was forgotten or remembered incorrectly
// state: State;          // The current state of the card (New, Learning, Review, Relearning)
// last_review?: Date;    // The most recent review date, if applicable

// The schema of user_preferences is as follows:
// user_id: uuid
// native_language: text
// active_language: text
// dark_mode: bool
// daily_card_goal: integer
// beta_distribution: jsonb                   // { [lang]: { a: number, b: number } }  (legacy; kept for backward-compat)
// language_ability_distribution: jsonb      // { [lang]: { mean: number, var: number } } (Rasch)
// test_cards_completed: jsonb               // { [lang]: number[] }  (list of used onboarding test card ids)
// starting_word_rank: jsonb                 // { [lang]: number }

/**
 * Get onboarding test state for current user and language.
 * Returns { a, b, completed, starting_word_rank }.
 */
export async function getOnboardingTestState(languageCode) {
  // DEPRECATED: This getter no longer reads beta_distribution (column does not exist).
  // It now reads Rasch state from language_ability_distribution and adapts the return shape.
  try {
    const { data: auth } = await supabase.auth.getUser();
    const uid = auth?.user?.id || null;
    if (!uid) {
      return { data: null, error: "Missing user id" };
    }

    const { data: pref, error } = await supabase
      .from("user_preferences")
      .select(
        "language_ability_distribution, test_cards_completed, starting_word_rank"
      )
      .eq("user_id", uid)
      .maybeSingle();

    if (error) {
      return { data: null, error };
    }

    // Rasch posterior storage (not used directly by this legacy API)
    const dist = pref?.language_ability_distribution || {};

    // Compute completed from array of used ids (fallback to numeric if legacy format present)
    const tcc = pref?.test_cards_completed || {};
    const used = tcc?.[languageCode];
    const completed = Array.isArray(used) ? used.length : Number(used ?? 0);

    const startMap = pref?.starting_word_rank || {};
    const starting_rank =
      startMap?.[languageCode] === undefined ? null : startMap?.[languageCode];

    // Legacy fields 'a' and 'b' are no longer used; return zeros to maintain shape
    const a = 0;
    const b = 0;

    // Optional: log once for diagnostics
    // eslint-disable-next-line no-console
    console.warn(
      "[getOnboardingTestState] DEPRECATED: returning legacy {a,b,completed} from Rasch-backed storage",
      {
        languageCode,
        completed,
        hasPosterior: !!dist?.[languageCode],
      }
    );

    return {
      data: { a, b, completed, starting_word_rank: starting_rank },
      error: null,
    };
  } catch (e) {
    return { data: null, error: e?.message || "Unexpected error" };
  }
}

/**
 * Increment onboarding test completed counter for a language (default +1).
 * Does not modify alpha/beta; use updateUserBetaDistribution for that.
 */
export async function incrementOnboardingTestCompleted(
  languageCode,
  delta = 1
) {
  try {
    const { data: auth } = await supabase.auth.getUser();
    const uid = auth?.user?.id || null;
    if (!uid) {
      return { success: false, error: "Missing user id" };
    }

    const { data: pref, error: selErr } = await supabase
      .from("user_preferences")
      .select("test_cards_completed")
      .eq("user_id", uid)
      .maybeSingle();

    if (selErr) {
      return { success: false, error: selErr };
    }

    const current = pref?.test_cards_completed || {};
    const nextVal = Number(current?.[languageCode] ?? 0) + Number(delta || 0);
    const updated = { ...current, [languageCode]: nextVal };

    const { error: upsertErr } = await supabase
      .from("user_preferences")
      .upsert({ user_id: uid, test_cards_completed: updated });

    if (upsertErr) {
      return { success: false, error: upsertErr };
    }

    return { success: true, data: updated };
  } catch (e) {
    return { success: false, error: e?.message || "Unexpected error" };
  }
}

/**
 * Set onboarding starting word rank for a language.
 */
export async function setOnboardingStartingRank(languageCode, rank) {
  try {
    const { data: auth } = await supabase.auth.getUser();
    const uid = auth?.user?.id || null;
    if (!uid) {
      return { success: false, error: "Missing user id" };
    }

    const { data: pref, error: selErr } = await supabase
      .from("user_preferences")
      .select("starting_word_rank")
      .eq("user_id", uid)
      .maybeSingle();

    if (selErr) {
      return { success: false, error: selErr };
    }

    const current = pref?.starting_word_rank || {};
    const updated = { ...current, [languageCode]: Number(rank) };

    const { error: upsertErr } = await supabase
      .from("user_preferences")
      .upsert({ user_id: uid, starting_word_rank: updated });

    if (upsertErr) {
      return { success: false, error: upsertErr };
    }

    return { success: true, data: updated };
  } catch (e) {
    return { success: false, error: e?.message || "Unexpected error" };
  }
}

/**
 * Get Rasch onboarding test state for current user and language.
 * Returns { mean, var, used_ids, starting_word_rank }.
 * - mean/var come from user_preferences.language_ability_distribution[language]
 * - used_ids comes from user_preferences.test_cards_completed[language] (array of ids)
 * - starting_word_rank if already set for language (number or null)
 */
export async function getRaschOnboardingState(languageCode) {
  try {
    const { data: auth } = await supabase.auth.getUser();
    const uid = auth?.user?.id || null;
    if (!uid) {
      return { data: null, error: "Missing user id" };
    }

    const { data: pref, error } = await supabase
      .from("user_preferences")
      .select(
        "language_ability_distribution, test_cards_completed, starting_word_rank"
      )
      .eq("user_id", uid)
      .maybeSingle();

    if (error) return { data: null, error };

    const dist = pref?.language_ability_distribution || {};
    const mean = Number(dist?.[languageCode]?.mean ?? 0);
    const variance = Number(dist?.[languageCode]?.var ?? 9);

    const tcc = pref?.test_cards_completed || {};
    const rawUsed = tcc?.[languageCode];
    const used_ids = Array.isArray(rawUsed)
      ? rawUsed.map((n) => Number(n)).filter((n) => Number.isFinite(n))
      : [];

    const startMap = pref?.starting_word_rank || {};
    const starting_rank =
      startMap?.[languageCode] === undefined ? null : startMap?.[languageCode];

    console.log("[getRaschOnboardingState] loaded", {
      languageCode,
      mean,
      var: variance,
      used_count: used_ids.length,
      starting_rank,
    });

    return {
      data: {
        mean,
        var: variance,
        used_ids,
        starting_word_rank: starting_rank,
      },
      error: null,
    };
  } catch (e) {
    return { data: null, error: e?.message || "Unexpected error" };
  }
}

/**
 * Update or insert language_ability_distribution for current user for a target language.
 * Shape: { [languageCode]: { mean: number, var: number }, ... }
 */
export async function updateUserAbilityDistribution(
  mean,
  variance,
  languageCode
) {
  try {
    if (
      typeof mean !== "number" ||
      typeof variance !== "number" ||
      !languageCode
    ) {
      return { success: false, error: "Invalid parameters" };
    }
    // Basic sanity clamp to avoid NaN/Infinity
    const mu = Number.isFinite(mean) ? mean : 0;
    const v = Number.isFinite(variance) && variance > 0 ? variance : 9;

    const { data: auth } = await supabase.auth.getUser();
    const uid = auth?.user?.id || null;
    if (!uid) {
      return { success: false, error: "Missing user id" };
    }

    // Read existing distribution to merge
    const { data: pref, error: selErr } = await supabase
      .from("user_preferences")
      .select("language_ability_distribution")
      .eq("user_id", uid)
      .maybeSingle();

    if (selErr) {
      console.error("Error reading language_ability_distribution:", selErr);
      return { success: false, error: selErr.message || "Read error" };
    }

    const current = pref?.language_ability_distribution || {};
    const updated = {
      ...current,
      [languageCode]: { mean: mu, var: v },
    };

    const { error: upsertErr } = await supabase
      .from("user_preferences")
      .upsert({ user_id: uid, language_ability_distribution: updated });

    if (upsertErr) {
      console.error("Error updating language_ability_distribution:", upsertErr);
      return { success: false, error: upsertErr.message || "Update error" };
    }

    console.log("[updateUserAbilityDistribution] updated", {
      languageCode,
      posterior: { mean: mu, var: v },
    });

    return { success: true, data: updated };
  } catch (e) {
    console.error(
      "Unexpected error updating language_ability_distribution:",
      e
    );
    return { success: false, error: e?.message || "Unexpected error" };
  }
}

/**
 * Append a used onboarding test card id for a language (no duplicates).
 * Stores in user_preferences.test_cards_completed[language] as an array of ids.
 */
export async function appendUsedTestCardId(languageCode, cardId) {
  try {
    const id = Number(cardId);
    if (!languageCode || !Number.isFinite(id)) {
      return { success: false, error: "Invalid parameters" };
    }

    const { data: auth } = await supabase.auth.getUser();
    const uid = auth?.user?.id || null;
    if (!uid) {
      return { success: false, error: "Missing user id" };
    }

    const { data: pref, error: selErr } = await supabase
      .from("user_preferences")
      .select("test_cards_completed")
      .eq("user_id", uid)
      .maybeSingle();

    if (selErr) {
      return { success: false, error: selErr };
    }

    const current = pref?.test_cards_completed || {};
    const existing = current?.[languageCode];
    const list = Array.isArray(existing) ? existing.slice() : []; // if legacy numeric format exists, ignore and start array fresh
    if (!list.includes(id)) list.push(id);

    const updated = { ...current, [languageCode]: list };

    const { error: upsertErr } = await supabase
      .from("user_preferences")
      .upsert({ user_id: uid, test_cards_completed: updated });

    if (upsertErr) {
      return { success: false, error: upsertErr };
    }

    console.log("[appendUsedTestCardId] updated used ids", {
      languageCode,
      appended: id,
      total: list.length,
    });

    return { success: true, data: updated };
  } catch (e) {
    return { success: false, error: e?.message || "Unexpected error" };
  }
}

/**
 * Fetch nearest probe card to current mean for language, honoring:
 * - Only every 5th id (id % 5 === 0)
 * - Exclude used ids array
 * - Select normalized default_ranking nearest to mean
 *
 * Strategy: probe an expanding window around target default_ranking.
 *
 * Supabase projects return at most 1,000 rows per request by default. Because
 * probe eligibility is evaluated client-side, each window must be paginated;
 * otherwise eligible cards after the first response page are invisible.
 */
export async function fetchNearestProbeCard(
  languageCode,
  mean,
  usedIds = [],
  opts = {}
) {
  const center = RANK_NORMALIZATION.center;
  const scale = RANK_NORMALIZATION.scale;
  try {
    const mu = Number.isFinite(mean) ? Number(mean) : 0;
    const used = Array.isArray(usedIds)
      ? new Set(usedIds.map(Number))
      : new Set();
    // Ability is intentionally allowed to move beyond the item bank while the
    // posterior is updated, but probe lookup must stay inside the bank's
    // 0-5,000 ranking domain. Without this clamp, a run of incorrect answers
    // can center every search window below rank 0 and exhaust the small slice
    // of easiest probes before the assessment is complete.
    const minRank = Number.isFinite(opts.minRank) ? Number(opts.minRank) : 0;
    const maxRank = Number.isFinite(opts.maxRank) ? Number(opts.maxRank) : 5000;
    const targetRaw = Math.max(
      minRank,
      Math.min(maxRank, Math.round(denormalizeRanking(mu, center, scale)))
    );

    const windows = Array.isArray(opts.windows)
      ? opts.windows
      : [100, 250, 500, 1000, 1500];
    const requestedPageSize = Number(opts.pageSize);
    const pageSize =
      Number.isInteger(requestedPageSize) && requestedPageSize > 0
        ? Math.min(requestedPageSize, 1000)
        : 1000;

    for (const halfWidth of windows) {
      const lo = Math.max(minRank, targetRaw - halfWidth);
      const hi = Math.min(maxRank, targetRaw + halfWidth);
      let best = null;
      let bestDist = Infinity;
      let fetchedCount = 0;
      let totalRows = null;
      let hasMoreRows = true;

      while (hasMoreRows) {
        const { data, error, count } = await supabase
          .from("flashcards")
          .select("id, default_ranking", { count: "exact" })
          .eq("language", languageCode)
          .gte("default_ranking", lo)
          .lte("default_ranking", hi)
          .order("default_ranking", { ascending: true })
          .order("id", { ascending: true })
          .range(fetchedCount, fetchedCount + pageSize - 1);

        if (error) {
          console.error("[fetchNearestProbeCard] query error", {
            languageCode,
            mean: mu,
            lo,
            hi,
            offset: fetchedCount,
            error: error?.message || error,
          });
          return { data: null, error };
        }

        const rows = Array.isArray(data) ? data : [];
        if (Number.isFinite(count)) totalRows = Number(count);

        for (const c of rows) {
          if (
            !c ||
            !Number.isFinite(c.id) ||
            !Number.isFinite(c.default_ranking)
          )
            continue;
          if (!isProbeEligibleId(c.id)) continue;
          if (used.has(c.id)) continue;

          const dNorm = normalizeRanking(c.default_ranking, center, scale);
          const dist = Math.abs(dNorm - mu);
          if (dist < bestDist) {
            best = c;
            bestDist = dist;
          }
        }

        fetchedCount += rows.length;
        hasMoreRows =
          rows.length > 0 &&
          (totalRows !== null
            ? fetchedCount < totalRows
            : rows.length === pageSize);
      }

      if (fetchedCount === 0) {
        console.log("[fetchNearestProbeCard] window empty", {
          languageCode,
          lo,
          hi,
        });
        continue;
      }

      if (best) {
        console.log("[fetchNearestProbeCard] selected", {
          languageCode,
          mean: mu,
          candidate: best,
          normalized_delta: normalizeRanking(
            best.default_ranking,
            center,
            scale
          ),
          dist: bestDist,
          window: { lo, hi },
          rowsScanned: fetchedCount,
        });
        return { data: best, error: null };
      }
    }

    // Fallback: no match found within windows
    console.warn("[fetchNearestProbeCard] no eligible candidate found", {
      languageCode,
      mean: mu,
      targetRaw,
      triedWindows: windows,
    });
    return { data: null, error: null };
  } catch (e) {
    console.error("[fetchNearestProbeCard] exception", {
      languageCode,
      mean,
      message: e?.message || String(e),
      stack: e?.stack || null,
    });
    return { data: null, error: e?.message || "Unexpected error" };
  }
}

/**
 * Fetch flashcards by IDs, preserving input order, including morpho metadata.
 */
export async function getFlashcardsByIds(ids = []) {
  console.log("[getFlashcardsByIds] start", {
    count: Array.isArray(ids) ? ids.length : null,
    sample: Array.isArray(ids) ? ids.slice(0, 10) : null,
  });
  try {
    if (!Array.isArray(ids) || ids.length === 0) {
      return { data: [], error: null };
    }

    console.log("[getFlashcardsByIds] querying supabase.flashcards");
    const { data, error } = await supabase
      .from("flashcards")
      .select(
        [
          "id",
          "language",
          "default_ranking",
          "language_word",
          "language_sentence",
          "language_word_mappings",
          "language_sentence_mappings",
          "language_word_audio",
          "language_sentence_audio",
          "language_word_synonyms",
          "pinyin",
          "part_of_speech",
          "tense",
          "number",
          "person",
          "measure_word",
          "gender",
        ].join(", ")
      )
      .in("id", ids);

    if (error) {
      console.error("[getFlashcardsByIds] query error", {
        message: error?.message || error,
        requested: Array.isArray(ids) ? ids.length : null,
      });
      return { data: [], error };
    }

    const map = new Map((data || []).map((c) => [c.id, c]));
    const ordered = ids.map((id) => map.get(id)).filter(Boolean);
    if (ordered.length !== ids.length) {
      const fetchedIds = new Set((data || []).map((c) => c?.id));
      const missingIds = ids.filter((id) => !fetchedIds.has(id));
      if (missingIds.length > 0) {
        console.warn("[getFlashcardsByIds] missing ids from response", {
          missingCount: missingIds.length,
          missingSample: missingIds.slice(0, 10),
        });
      }
    }
    console.log("[getFlashcardsByIds] ordered result", {
      requested: ids.length,
      loaded: ordered.length,
      firstLoadedIds: ordered.slice(0, 5).map((c) => c?.id),
    });
    return { data: ordered, error: null };
  } catch (e) {
    console.error("[getFlashcardsByIds] unexpected error", {
      message: e?.message || String(e),
      stack: e?.stack || null,
      idsSample: Array.isArray(ids) ? ids.slice(0, 10) : null,
    });
    return { data: [], error: e?.message || "Unexpected error" };
  }
}

// Function that will accept the user's UID and a language
// We will use .upsert() and it will create a new row if it doesn't exist and will ignore the row if it does already exist
export async function createUserFlashcards(language, UID) {
  console.log("Creating user flashcards for:", UID, "language:", language);

  // Resolve language from user_preferences.active_language if not provided
  if (!language) {
    try {
      const { data: pref, error: prefErr } = await supabase
        .from("user_preferences")
        .select("active_language")
        .eq("user_id", UID)
        .maybeSingle();
      if (!prefErr && pref?.active_language) {
        language = pref.active_language;
        console.log("Using active_language from user_preferences:", language);
      } else {
        console.error(
          "No active_language found for user; cannot create user flashcards"
        );
        return false;
      }
    } catch (e) {
      console.error("Error resolving active_language:", e);
      return false;
    }
  }

  const pageSize = 1000; // keep <= your PostgREST max-rows
  let from = 0;
  let totalProcessed = 0;
  let page = 0;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    page += 1;
    const to = from + pageSize - 1;

    const { data: flashcards, error: cardError } = await supabase
      .from("flashcards")
      .select("id, default_ranking")
      .eq("language", language)
      .order("default_ranking", { ascending: true })
      .range(from, to);

    if (cardError) {
      console.error("Error fetching flashcards (page", page, "):", cardError);
      return cardError;
    }

    if (!flashcards || flashcards.length === 0) {
      console.log("No more flashcards to process. Done.");
      break;
    }

    // Prepare upsert rows for this page
    const userFlashcards = flashcards.map((card) => ({
      user_id: UID,
      id: card.id,
      default_ranking: card.default_ranking,
      language: language,
      // Setting the due date to right now
      due: new Date().toISOString(),
      // other fields rely on DB defaults
    }));

    // Upsert in chunks in case payload limits are tighter than pageSize
    const chunkSize = 1000;
    for (let i = 0; i < userFlashcards.length; i += chunkSize) {
      const chunk = userFlashcards.slice(i, i + chunkSize);
      const { error: upsertError } = await supabase
        .from("user_cards")
        .upsert(chunk, {
          onConflict: "user_id,id", // needs unique (user_id,id)
          ignoreDuplicates: true,
        });

      if (upsertError) {
        console.error("Upsert error (page", page, "):", upsertError);
        return upsertError;
      }
    }

    totalProcessed += flashcards.length;
    console.log(
      `Page ${page}: processed ${flashcards.length} (total ${totalProcessed}).`
    );

    // Advance to next page
    from += pageSize;

    // If the page returned fewer than pageSize rows, we've reached the end.
    if (flashcards.length < pageSize) break;
  }

  console.log(`User flashcards ensured: ${totalProcessed} processed total.`);
  return true;
}

// Function that will get up to `limit` flashcards for the user
export async function getUserFlashcards(
  UID,
  excludeIds = [],
  limit = 10,
  user_language
) {
  if (!UID) {
    console.error("getUserFlashcards called without a valid UID");
    return { data: [], error: null };
  }
  console.log("[getUserFlashcards] start", {
    UID,
    limit,
    excludeIdsCount: Array.isArray(excludeIds) ? excludeIds.length : null,
    user_language_param: user_language,
  });
  const cols =
    "id, due, stability, difficulty, scheduled_days, reps, lapses, state, last_review, default_ranking";

  // Resolve user language: use provided user_language or fetch from user_preferences.active_language
  let userLang = user_language;
  if (!userLang) {
    try {
      const { data: pref, error: prefErr } = await supabase
        .from("user_preferences")
        .select("active_language")
        .eq("user_id", UID)
        .maybeSingle();
      if (!prefErr) {
        userLang = pref?.active_language || null;
        console.log("[getUserFlashcards] resolved userLang from preferences", {
          active_language: pref?.active_language,
        });
      } else {
        console.error("Error fetching user active_language:", prefErr);
      }
    } catch (e) {
      console.error("Exception while fetching active_language:", e);
    }
  } else {
    console.log("[getUserFlashcards] using provided user_language", {
      userLang,
    });
  }
  if (!userLang) {
    console.error("getUserFlashcards: missing user language");
    return { data: [], error: null };
  }

  // First, get due cards that have been reviewed before
  let q1 = supabase
    .from("user_cards")
    .select(cols)
    .eq("user_id", UID)
    .eq("language", userLang)
    .gte("reps", 1) // reviewed before
    .lte("due", new Date().toISOString())
    .order("due", { ascending: true });

  if (excludeIds.length > 0) {
    q1 = q1.not("id", "in", `(${excludeIds.join(",")})`);
  }

  const { data: dueReviewed, error: e1 } = await q1.limit(limit);
  console.log("[getUserFlashcards] dueReviewed fetch", {
    count: Array.isArray(dueReviewed) ? dueReviewed.length : 0,
    error: e1?.message || null,
  });
  if (e1) {
    console.error(
      "Error fetching flashcards (dueReviewed):",
      e1?.message || e1,
      e1
    );
    return { data: null, error: e1 };
  }

  let flashcards = dueReviewed ?? [];

  // If we didn't reach the limit, top up with never-reviewed cards by default_ranking
  if (flashcards.length < limit) {
    const remaining = limit - flashcards.length;

    let q2 = supabase
      .from("user_cards")
      .select(cols)
      .eq("user_id", UID)
      .eq("language", userLang)
      .eq("reps", 0)
      .order("default_ranking", { ascending: true });

    if (excludeIds.length > 0) {
      q2 = q2.not("id", "in", `(${excludeIds.join(",")})`);
    }

    const { data: fillers, error: e2 } = await q2.limit(remaining);
    console.log("[getUserFlashcards] never-reviewed fetch", {
      requested: remaining,
      count: Array.isArray(fillers) ? fillers.length : 0,
      error: e2?.message || null,
    });
    if (e2) {
      console.error(
        "Error fetching additional flashcards:",
        e2?.message || e2,
        e2
      );
      return { data: null, error: e2 };
    }

    flashcards = [...flashcards, ...(fillers ?? [])];
  }

  console.log("[getUserFlashcards] total user_cards selected", {
    count: flashcards.length,
  });

  // If no cards, short-circuit
  if (flashcards.length === 0) {
    console.log("[getUserFlashcards] no user_cards found, returning empty set");
    return { data: [], error: null };
  }

  // Pull the flashcard rows to merge details
  const flashcardIds = flashcards.map((c) => c.id);
  console.log("[getUserFlashcards] fetching flashcards details", {
    idsCount: flashcardIds.length,
    userLang,
  });
  const { data: flashcardData, error: e3 } = await supabase
    .from("flashcards")
    .select("*")
    .in("id", flashcardIds)
    .eq("language", userLang);

  console.log("[getUserFlashcards] flashcards details result", {
    count: Array.isArray(flashcardData) ? flashcardData.length : 0,
    error: e3?.message || null,
  });

  if (e3) {
    console.error("Error fetching flashcard data:", e3?.message || e3, e3);
    return { data: null, error: e3 };
  }

  // Merge user_cards with flashcards on id and keep only matching language
  const merged = flashcards
    .map((uc) => {
      const f = flashcardData.find((x) => x.id === uc.id) ?? {};
      return { ...uc, ...f };
    })
    .filter((row) => row.language === userLang);

  console.log("[getUserFlashcards] merged result", {
    mergedCount: merged.length,
  });

  return { data: merged, error: null };
}

// Function to update user flashcard data after review
export async function updateUserFlashcard(user_id, card) {
  if (!card) {
    console.error("Invalid card data when updating flashcard");
    return;
  }

  if (!user_id) {
    console.error("Invalid user ID when updating flashcard");
    return;
  }

  // Checking if any of the fields are undefined, and if they are, we'll fill them in with NULL
  card.due = card.due || null;
  card.stability = card.stability || null;
  card.difficulty = card.difficulty || null;
  card.scheduled_days = card.scheduled_days || null;
  card.learning_steps = card.learning_steps || null;
  card.reps = card.reps || null;
  card.lapses = card.lapses || null;
  card.state = card.state || null;
  card.last_review = card.last_review || null;

  const { data, error } = await supabase
    .from("user_cards")
    .update({
      due: card.due,
      stability: card.stability,
      difficulty: card.difficulty,
      scheduled_days: card.scheduled_days,
      learning_steps: card.learning_steps,
      reps: card.reps,
      lapses: card.lapses,
      state: card.state,
      last_review: card.last_review,
    })
    .eq("user_id", user_id)
    .eq("id", card.id);

  if (error) {
    console.error("Error updating flashcard:", error);
    throw error;
  }

  console.log("Flashcard updated successfully");
  return data;
}

// Function to update card_actions table after review
export async function updateCardActions(
  user_id,
  card_id,
  correct,
  durationMs,
  durationTypingMs,
  correctAnswerCharLen,
  onMobile,
  durationRecallMs
) {
  if (!user_id || !card_id) {
    console.error("Invalid user ID or card ID when updating card actions");
    return;
  }

  // Resolve language code for card_actions.language
  let languageCode = null;
  try {
    const { data: uc } = await supabase
      .from("user_cards")
      .select("language")
      .eq("user_id", user_id)
      .eq("id", card_id)
      .maybeSingle();
    languageCode = uc?.language || null;
    if (!languageCode) {
      const { data: fc } = await supabase
        .from("flashcards")
        .select("language")
        .eq("id", card_id)
        .maybeSingle();
      languageCode = fc?.language || null;
    }
  } catch (_e) {
    // ignore; leave languageCode as null
  }

  const userFlashcardAction = {
    user_id: user_id,
    card_id: card_id,
    language: languageCode,
    correct: !!correct,
    // Requires column card_actions.duration_ms (integer or numeric, nullable)
    duration_ms:
      typeof durationMs === "number"
        ? Math.max(0, Math.floor(durationMs))
        : null,
    // Requires column card_actions.duration_typing_ms (integer), nullable
    duration_typing_ms:
      typeof durationTypingMs === "number"
        ? Math.max(0, Math.floor(durationTypingMs))
        : null,
    // Requires column card_actions.duration_recall_ms (integer), nullable
    duration_recall_ms:
      typeof durationRecallMs === "number"
        ? Math.max(0, Math.floor(durationRecallMs))
        : null,
    // Requires column card_actions.correct_answer_character_length (integer), nullable
    correct_answer_character_length:
      typeof correctAnswerCharLen === "number"
        ? Math.max(0, Math.floor(correctAnswerCharLen))
        : null,
    // Whether the user answered on mobile (explicit arg wins; fallback to UA detection)
    on_mobile:
      typeof onMobile === "boolean"
        ? onMobile
        : (typeof navigator !== "undefined" &&
            /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
              navigator.userAgent || ""
            )) ||
          false,
    // date is of the datatype: timestamptz
    date: new Date().toISOString(),
  };

  // Inserting a new row for the card actions
  const { data, error } = await supabase
    .from("card_actions")
    .insert([userFlashcardAction]);

  if (error) {
    console.error("Error updating card actions:", error);
    throw error;
  }

  console.log("Card actions updated successfully");
  console.log(data);
}

export async function countTodaysCards(user_id) {
  // This allows us to use the users timezone rather than UTC
  const startLocal = new Date();
  startLocal.setHours(0, 0, 0, 0);
  const endLocal = new Date();
  endLocal.setHours(24, 0, 0, 0);

  const { data, error } = await supabase
    .from("card_actions")
    .select("card_id")
    .eq("user_id", user_id)
    .gte("date", startLocal.toISOString())
    .lt("date", endLocal.toISOString());

  if (error) {
    console.error("Error counting today's cards:", error);
    throw error;
  }

  console.log("Today's cards counted successfully");
  console.log(data);

  return data.length;
}

export async function getTotalAnsweredCards(user_id) {
  if (!user_id) return 0;
  try {
    const res = await supabase
      .from("card_actions")
      .select("*", { count: "exact", head: false })
      .eq("user_id", user_id)
      .limit(0);

    const { count, error } = res;

    if (!error && typeof count === "number") {
      return count;
    }
    console.error("Error counting total answered cards:", error);
    return 0;
  } catch (e) {
    console.error("Exception counting total answered cards:", e);
    return 0;
  }
}

export async function updateUserGoal(user_id, goal) {
  if (!user_id || !goal) {
    console.error("Invalid user ID or goal when updating user goal");
    return;
  }

  // Using an upsert to update or insert the user's goal
  const { data, error } = await supabase
    .from("user_preferences")
    .upsert({ user_id: user_id, daily_card_goal: goal });

  if (error) {
    console.error("Error updating user goal:", error);
    throw error;
  }

  console.log("User goal updated successfully");
  console.log(data);
}

export async function getUserDailyGoal(user_id) {
  if (!user_id) {
    console.error("Invalid user ID when getting user daily goal");
    return;
  }

  const { data, error } = await supabase
    .from("user_preferences")
    .select("daily_card_goal")
    .eq("user_id", user_id);

  if (error) {
    console.error("Error getting user daily goal:", error);
    throw error;
  }

  if (!data || data.length === 0) {
    // Create default user_preferences row with a 2-week free trial window
    const { error: upsertErr } = await supabase
      .from("user_preferences")
      .upsert({
        user_id: user_id,
        price_id: "Free trial",
        subscription_status: "Free trial",
        // current_period_end: We will rely on the default value here
        auto_renew: false,
      });

    if (upsertErr) {
      console.error("Error creating default user_preferences row:", upsertErr);
      throw upsertErr;
    }

    console.log("Created default user_preferences row with free trial");
    return 0;
  }

  console.log("User daily goal retrieved successfully");
  console.log(data);

  return data[0]?.daily_card_goal || 0;
}

export async function getProgressBarValues(user_id) {
  if (!user_id) {
    console.error("Invalid user ID when getting progress bar values");
    return;
  }

  console.log("Fetching progress bar values");

  // Resolve the user's active language to scope counts
  let userLang = null;
  try {
    const { data: pref, error: prefErr } = await supabase
      .from("user_preferences")
      .select("active_language")
      .eq("user_id", user_id)
      .maybeSingle();
    if (!prefErr && pref) {
      userLang = pref.active_language || null;
    }
  } catch (e) {
    // Non-fatal: if we can't resolve language, fallback to cross-language counts
    console.error("Failed to resolve active language for progress bar:", e);
  }

  // Build language-scoped queries
  let q0 = supabase
    .from("user_cards")
    .select("id", { count: "exact", head: true })
    .eq("user_id", user_id)
    .eq("state", 0)
    .gte("reps", 1);
  if (userLang) q0 = q0.eq("language", userLang);
  const { count: zerosCount, error: e0 } = await q0;

  let q1 = supabase
    .from("user_cards")
    .select("id", { count: "exact", head: true })
    .eq("user_id", user_id)
    .eq("state", 1);
  if (userLang) q1 = q1.eq("language", userLang);
  const { count: onesCount, error: e1 } = await q1;

  let q2 = supabase
    .from("user_cards")
    .select("id", { count: "exact", head: true })
    .eq("user_id", user_id)
    .eq("state", 2);
  if (userLang) q2 = q2.eq("language", userLang);
  const { count: twosCount, error: e2 } = await q2;

  let q3 = supabase
    .from("user_cards")
    .select("id", { count: "exact", head: true })
    .eq("user_id", user_id)
    .eq("state", 3);
  if (userLang) q3 = q3.eq("language", userLang);
  const { count: threesCount, error: e3 } = await q3;

  if (e0 || e1 || e2 || e3) {
    console.error("Error counting cards:", e0, e1, e2, e3);
    throw e0 || e1 || e2 || e3;
  }

  console.log("Progress bar values fetched successfully");
  console.log(
    "Zeros:",
    zerosCount,
    "Ones:",
    onesCount,
    "Twos:",
    twosCount,
    "Threes:",
    threesCount
  );

  return {
    total_zeros: zerosCount ?? 0,
    total_ones: onesCount ?? 0,
    total_twos: twosCount ?? 0,
    total_threes: threesCount ?? 0,
  };
}

const STATS_PAGE_SIZE = 1000;

function localDayKey(value) {
  const d = value instanceof Date ? value : new Date(value);
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${yr}-${mo}-${da}`;
}

/**
 * Daily streak: number of consecutive days with at least one review action.
 * A streak through yesterday remains active until the user has a chance to
 * review today.
 */
export async function getDailyStreak(user_id) {
  if (!user_id) return 0;

  // Page backward until the first missing calendar day is proven. This avoids
  // both the old 60-day cap and downloading a user's entire review history.
  const activeDays = new Set();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (let from = 0; ; from += STATS_PAGE_SIZE) {
    const { data, error } = await supabase
      .from("card_actions")
      .select("date")
      .eq("user_id", user_id)
      .order("date", { ascending: false })
      .range(from, from + STATS_PAGE_SIZE - 1);

    if (error) {
      console.error("Error fetching actions for streak:", error);
      throw error;
    }

    for (const row of data || []) activeDays.add(localDayKey(row.date));

    let streak = 0;
    let offset = activeDays.has(localDayKey(today)) ? 0 : 1;
    let missingDay = null;
    for (;; offset++) {
      const d = new Date(today);
      d.setDate(today.getDate() - offset);
      if (activeDays.has(localDayKey(d))) streak++;
      else {
        missingDay = d;
        break;
      }
    }

    if (!data || data.length < STATS_PAGE_SIZE) return streak;

    const oldestLoaded = new Date(data[data.length - 1].date);
    oldestLoaded.setHours(0, 0, 0, 0);
    if (oldestLoaded < missingDay) return streak;
  }
}

/**
 * Accuracy over the last 7 days. Returns { correct, total, percent }
 */
export async function getAccuracyLast7Days(user_id) {
  // Scope to user's active language
  const { data: pref } = await supabase
    .from("user_preferences")
    .select("active_language")
    .eq("user_id", user_id)
    .maybeSingle();
  const userLang = pref?.active_language || null;

  const langZero = { correct: 0, total: 0, percent: 0 };
  if (!user_id || !userLang) return langZero;

  const { data: allowedCards, error: allowedErr } = await supabase
    .from("user_cards")
    .select("id")
    .eq("user_id", user_id)
    .eq("language", userLang);
  if (allowedErr) {
    console.error("Error fetching cards for language filter:", allowedErr);
    throw allowedErr;
  }
  const allowedIds = (allowedCards || []).map((r) => r.id);
  if (allowedIds.length === 0) return langZero;
  if (!user_id) return { correct: 0, total: 0, percent: 0 };

  const start = new Date();
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - 6); // include today -> 7 days window

  const { data, error } = await supabase
    .from("card_actions")
    .select("correct")
    .eq("user_id", user_id)
    .in("card_id", allowedIds)
    .gte("date", start.toISOString());

  if (error) {
    console.error("Error fetching actions for accuracy:", error);
    throw error;
  }

  const rows = data || [];
  const total = rows.length;
  const correct = rows.filter((r) => r.correct === true).length;
  const percent = total > 0 ? Math.round((correct / total) * 100) : 0;
  return { correct, total, percent };
}

/**
 * Average time per card over the last 7 days (milliseconds).
 * Requires card_actions.duration_ms column (integer), nullable.
 */
export async function getAvgTimePerCardLast7Days(user_id) {
  // Scope to user's active language
  const { data: pref } = await supabase
    .from("user_preferences")
    .select("active_language")
    .eq("user_id", user_id)
    .maybeSingle();
  const userLang = pref?.active_language || null;

  if (!user_id || !userLang) return 0;

  const { data: allowedCards, error: allowedErr } = await supabase
    .from("user_cards")
    .select("id")
    .eq("user_id", user_id)
    .eq("language", userLang);
  if (allowedErr) {
    console.error("Error fetching cards for language filter:", allowedErr);
    throw allowedErr;
  }
  const allowedIds = (allowedCards || []).map((r) => r.id);
  if (allowedIds.length === 0) return 0;
  if (!user_id) return 0;

  const start = new Date();
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - 6);

  const { data, error } = await supabase
    .from("card_actions")
    .select("duration_ms")
    .eq("user_id", user_id)
    .in("card_id", allowedIds)
    .gte("date", start.toISOString())
    .not("duration_ms", "is", null);

  if (error) {
    console.error("Error fetching durations:", error);
    throw error;
  }

  const rows = (data || [])
    .map((r) => Number(r.duration_ms))
    .filter((n) => Number.isFinite(n) && n >= 0);
  if (rows.length === 0) return 0;
  const sum = rows.reduce((a, b) => a + b, 0);
  return Math.round(sum / rows.length);
}

async function getNewWordFirstActionsInLastDays(user_id, userLang, days) {
  if (!user_id || !userLang || days <= 0) return [];
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - (days - 1));
  const startISO = start.toISOString();

  // Collect all candidate cards from the window, not just the first 1,000
  // action rows returned by PostgREST.
  const candidateDates = new Map();
  for (let from = 0; ; from += STATS_PAGE_SIZE) {
    const { data, error } = await supabase
      .from("card_actions")
      .select("card_id")
      .eq("user_id", user_id)
      .eq("language", userLang)
      .gte("date", startISO)
      .order("date", { ascending: false })
      .range(from, from + STATS_PAGE_SIZE - 1);

    if (error) {
      console.error("Error fetching recent actions:", error);
      throw error;
    }

    for (const row of data || []) {
      const existing = candidateDates.get(row.card_id);
      if (!existing || row.date < existing) {
        candidateDates.set(row.card_id, row.date);
      }
    }
    if (!data || data.length < STATS_PAGE_SIZE) break;
  }

  const candidates = Array.from(candidateDates.keys());
  if (candidates.length === 0) return [];

  // Query candidates in bounded groups and paginate earlier actions. Stop a
  // group as soon as every card in it has been found. This avoids the former
  // false positives caused by silently truncating the earlier-action query.
  const cardsWithEarlierActions = new Set();
  const candidateBatchSize = 100;
  for (let i = 0; i < candidates.length; i += candidateBatchSize) {
    const batch = candidates.slice(i, i + candidateBatchSize);
    const foundInBatch = new Set();

    for (let from = 0; ; from += STATS_PAGE_SIZE) {
      const { data, error } = await supabase
        .from("card_actions")
        .select("card_id")
        .eq("user_id", user_id)
        .eq("language", userLang)
        .lt("date", startISO)
        .in("card_id", batch)
        .order("date", { ascending: false })
        .range(from, from + STATS_PAGE_SIZE - 1);

      if (error) {
        console.error("Error checking earlier actions:", error);
        throw error;
      }

      for (const row of data || []) foundInBatch.add(row.card_id);
      if (
        foundInBatch.size === batch.length ||
        !data ||
        data.length < STATS_PAGE_SIZE
      ) {
        break;
      }
    }

    for (const cardId of foundInBatch) cardsWithEarlierActions.add(cardId);
  }

  return candidates
    .filter((id) => !cardsWithEarlierActions.has(id))
    .map((card_id) => ({ card_id, date: candidateDates.get(card_id) }));
}

async function getNewWordsLearnedInLastDays(user_id, userLang, days) {
  const firstActions = await getNewWordFirstActionsInLastDays(
    user_id,
    userLang,
    days
  );
  return firstActions.length;
}

/**
 * Count active-language cards whose genuine first-ever action occurred during
 * today plus the previous six calendar days.
 */
export async function getNewWordsLearnedThisWeek(user_id) {
  const { data: pref } = await supabase
    .from("user_preferences")
    .select("active_language")
    .eq("user_id", user_id)
    .maybeSingle();
  const userLang = pref?.active_language || null;
  return getNewWordsLearnedInLastDays(user_id, userLang, 7);
}

export async function getNewWordsTrend(user_id, days = 30) {
  if (!user_id || days <= 0) return [];
  const { data: pref } = await supabase
    .from("user_preferences")
    .select("active_language")
    .eq("user_id", user_id)
    .maybeSingle();
  const userLang = pref?.active_language || null;
  if (!userLang) return [];

  const start = new Date();
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - (days - 1));
  const firstActions = await getNewWordFirstActionsInLastDays(
    user_id,
    userLang,
    days
  );
  const counts = new Map();
  for (const action of firstActions) {
    const key = localDayKey(action.date);
    counts.set(key, (counts.get(key) || 0) + 1);
  }

  return Array.from({ length: days }, (_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    const key = localDayKey(date);
    return { date: key, count: counts.get(key) || 0 };
  });
}

/**
 * Growth trend over the last `days` (default 30).
 * Returns { daily: [{date, count}], cumulative: [{date, count}] }
 * where 'count' is number of new words (first-ever actions) that day within the window,
 * and cumulative sums those daily counts within the window.
 */
export async function getGrowthTrend(user_id, days = 30) {
  if (!user_id) return { daily: [], cumulative: [] };

  const start = new Date();
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - (days - 1));
  const startISO = start.toISOString();

  const { data, error } = await supabase
    .from("card_actions")
    .select("card_id, date")
    .eq("user_id", user_id)
    .gte("date", startISO)
    .order("date", { ascending: true });

  if (error) {
    console.error("Error fetching growth actions:", error);
    throw error;
  }

  const toKey = (d) => {
    const yr = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    const da = String(d.getDate()).padStart(2, "0");
    return `${yr}-${mo}-${da}`;
  };

  // Earliest action per card within the window
  const firstByCard = new Map();
  for (const r of data || []) {
    const dt = new Date(r.date);
    dt.setHours(0, 0, 0, 0);
    const k = toKey(dt);
    if (!firstByCard.has(r.card_id)) {
      firstByCard.set(r.card_id, k);
    }
  }

  // Initialize all days in window
  const dailyMap = new Map();
  for (let i = 0; i < days; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    dailyMap.set(toKey(d), 0);
  }

  // Count new cards per day (within window)
  for (const dayKey of firstByCard.values()) {
    if (dailyMap.has(dayKey)) {
      dailyMap.set(dayKey, (dailyMap.get(dayKey) || 0) + 1);
    }
  }

  const daily = [];
  const cumulative = [];
  let running = 0;
  for (let i = 0; i < days; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const k = toKey(d);
    const c = dailyMap.get(k) || 0;
    running += c;
    daily.push({ date: k, count: c });
    cumulative.push({ date: k, count: running });
  }

  return { daily, cumulative };
}

/**
 * Upcoming workload for the next `days` days starting today, plus overdue count.
 * Returns: { overdue: number, days: [{ date: 'YYYY-MM-DD', count: number }] }
 */
export async function getUpcomingWorkload(user_id, days = 7) {
  if (!user_id) return { overdue: 0, days: [] };

  // Scope to user's active language
  const { data: pref } = await supabase
    .from("user_preferences")
    .select("active_language")
    .eq("user_id", user_id)
    .maybeSingle();
  const userLang = pref?.active_language || null;
  if (!userLang) return { overdue: 0, days: [] };

  const start = new Date();
  start.setHours(0, 0, 0, 0);

  const end = new Date(start);
  end.setDate(start.getDate() + (days - 1));
  end.setHours(23, 59, 59, 999);

  // Upcoming due items within the window
  const { data: upcoming, error: e1 } = await supabase
    .from("user_cards")
    .select("due")
    .eq("user_id", user_id)
    .eq("language", userLang)
    .gte("due", start.toISOString())
    .lte("due", end.toISOString());

  if (e1) {
    console.error("Error fetching upcoming workload:", e1);
    throw e1;
  }

  // Overdue: due before today
  const { count: overdueCount, error: e2 } = await supabase
    .from("user_cards")
    .select("id", { count: "exact", head: true })
    .eq("user_id", user_id)
    .eq("language", userLang)
    .lt("due", start.toISOString())
    .gte("reps", 1);

  if (e2) {
    console.error("Error counting overdue workload:", e2);
    throw e2;
  }

  const toKey = (d) => {
    const yr = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    const da = String(d.getDate()).padStart(2, "0");
    return `${yr}-${mo}-${da}`;
  };

  // Initialize buckets for each day in the window
  const buckets = new Map();
  for (let i = 0; i < days; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    buckets.set(toKey(d), 0);
  }

  for (const row of upcoming || []) {
    if (!row?.due) continue;
    const d = new Date(row.due);
    d.setHours(0, 0, 0, 0);
    const k = toKey(d);
    if (buckets.has(k)) buckets.set(k, (buckets.get(k) || 0) + 1);
  }

  const resultDays = [];
  for (let i = 0; i < days; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const k = toKey(d);
    resultDays.push({ date: k, count: buckets.get(k) || 0 });
  }

  return { overdue: overdueCount ?? 0, days: resultDays };
}

/**
 * Count remaining "new" cards for a user (cards with reps = 0).
 */
export async function getRemainingNewCards(user_id, userLang) {
  if (!user_id || !userLang) return 0;

  const { count, error } = await supabase
    .from("user_cards")
    .select("id", { count: "exact", head: true })
    .eq("user_id", user_id)
    .eq("language", userLang)
    .eq("reps", 0);

  if (error) {
    console.error("Error counting remaining new cards:", error);
    throw error;
  }
  return count ?? 0;
}

/**
 * Average number of new cards learned per day over the last `days` (default 14).
 * "New" means first-ever action for that card in card_actions.
 */
export async function getAverageNewPerDay(user_id, days = 14, userLang) {
  if (!user_id || !userLang) return 0;
  const totalNew = await getNewWordsLearnedInLastDays(
    user_id,
    userLang,
    days
  );
  const avg = totalNew / days;
  return Math.round(avg * 100) / 100; // two decimals
}

/**
 * Estimate days remaining to complete all cards (reach at least one review per card),
 * based on remaining new cards and average daily new cards learned.
 * Returns: { remaining_new, avg_new_per_day, days, eta }
 */
export async function getCompletionEstimate(user_id, avgWindowDays = 14) {
  if (!user_id) {
    return {
      remaining_new: 0,
      avg_new_per_day: 0,
      days: null,
      eta: null,
    };
  }

  // Read active_language from user_preferences
  const { data: pref } = await supabase
    .from("user_preferences")
    .select("active_language")
    .eq("user_id", user_id)
    .maybeSingle();

  const userLang = pref?.active_language || null;

  if (!userLang) {
    // Without an active language, we cannot estimate per-language completion
    return {
      remaining_new: 0,
      avg_new_per_day: 0,
      days: null,
      eta: null,
    };
  }

  const [remaining, avgNew] = await Promise.all([
    getRemainingNewCards(user_id, userLang),
    getAverageNewPerDay(user_id, avgWindowDays, userLang),
  ]);

  const effectiveAvg = Number(avgNew) || 0;
  if (effectiveAvg <= 0) {
    return {
      remaining_new: remaining,
      avg_new_per_day: 0,
      days: null,
      eta: null,
    };
  }

  const daysRemaining = Math.ceil(remaining / effectiveAvg);

  const etaDate = new Date();
  etaDate.setHours(0, 0, 0, 0);
  etaDate.setDate(etaDate.getDate() + daysRemaining);

  return {
    remaining_new: remaining,
    avg_new_per_day: Math.round(effectiveAvg * 100) / 100,
    days: daysRemaining,
    eta: etaDate.toISOString(),
  };
}

export async function getAvgReviewsPerCardLast7Days(user_id) {
  // Scope to user's active language
  const { data: pref } = await supabase
    .from("user_preferences")
    .select("active_language")
    .eq("user_id", user_id)
    .maybeSingle();
  const userLang = pref?.active_language || null;

  if (!user_id || !userLang) return 0;

  // 7-day window including today
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - 6);
  const startISO = start.toISOString();

  // Allowed cards in the active language
  const { data: allowedCards, error: allowedErr } = await supabase
    .from("user_cards")
    .select("id")
    .eq("user_id", user_id)
    .eq("language", userLang);

  if (allowedErr) {
    console.error("Error fetching cards for language filter:", allowedErr);
    throw allowedErr;
  }

  const allowedIds = (allowedCards || []).map((r) => r.id);
  if (allowedIds.length === 0) return 0;

  // All actions on those cards in the window
  const { data, error } = await supabase
    .from("card_actions")
    .select("card_id")
    .eq("user_id", user_id)
    .in("card_id", allowedIds)
    .gte("date", startISO);

  if (error) {
    console.error("Error fetching actions for avg reviews/card:", error);
    throw error;
  }

  const rows = data || [];
  if (rows.length === 0) return 0;

  const totalReviews = rows.length;
  const distinctCards = new Set(rows.map((r) => r.card_id)).size || 0;
  if (distinctCards === 0) return 0;

  const avg = totalReviews / distinctCards;
  return Math.round(avg * 100) / 100;
}

/**
 * Timing-based FSRS rating selection
 * - For incorrect answers: always "Again".
 * - For correct answers: classify "Hard" | "Good" | "Easy" using t_norm thresholds.
 *
 * t_norm = (duration_recall_ms + duration_typing_ms) / (a + b * L)
 * where L = correct_answer_character_length
 *
 * Percentiles (q_20, q_80) are stored per (id, language_code, on_mobile) in user_language_stats.
 * Recalculate once per day using latest a and b over the most recent HISTORY_SAMPLE_LIMIT correct actions.
 * Blend empirical percentiles with priors when n < MIX_TARGET_N:
 *   w = min(1, n / MIX_TARGET_N)
 *   q_20 = (1 - w) * PRIOR_Q20 + w * EMP_Q20
 *   q_80 = (1 - w) * PRIOR_Q80 + w * EMP_Q80
 */

// Tunable constants (heuristics and limits)
const TIMING_PRIOR_Q20 = 0.85; // heuristic prior for 20th percentile of t_norm
const TIMING_PRIOR_Q80 = 1.2; // heuristic prior for 80th percentile of t_norm
const MIX_TARGET_N = 500; // target sample size for fully trusting empirical percentiles
const HISTORY_SAMPLE_LIMIT = 1500; // max recent rows used to recompute percentiles

// Device defaults for a and b (ms and ms/char) if user_language_stats row is missing
const DEFAULT_PARAMS_DESKTOP = { a: 3000, b: 180 };
const DEFAULT_PARAMS_MOBILE = { a: 3500, b: 200 };

/**
 * Compute simple percentile by nearest-rank (p in [0,1]).
 */
function percentileNearestRank(values, p) {
  const arr = (Array.isArray(values) ? values.slice() : [])
    .filter((x) => Number.isFinite(x))
    .sort((a, b) => a - b);
  if (arr.length === 0) return NaN;
  const idx = Math.min(
    arr.length - 1,
    Math.max(0, Math.round(p * (arr.length - 1)))
  );
  return arr[idx];
}

/**
 * Ensure q20 < q80 with tiny separation.
 */
function orderQuantiles(q20, q80) {
  let a = Number(q20);
  let b = Number(q80);
  if (!Number.isFinite(a)) a = TIMING_PRIOR_Q20;
  if (!Number.isFinite(b)) b = TIMING_PRIOR_Q80;
  if (a >= b) {
    const m = (a + b) / 2;
    a = Math.max(0, m - 1e-6);
    b = m + 1e-6;
  }
  return { q20: a, q80: b };
}

/**
 * Blend empirical and prior percentiles with weight w = min(1, n / MIX_TARGET_N).
 */
function blendedQuantiles(emp20, emp80, n) {
  const w = Math.min(1, Math.max(0, Number(n) || 0) / MIX_TARGET_N);
  const e20 = Number.isFinite(emp20) ? emp20 : TIMING_PRIOR_Q20;
  const e80 = Number.isFinite(emp80) ? emp80 : TIMING_PRIOR_Q80;
  const q20 = (1 - w) * TIMING_PRIOR_Q20 + w * e20;
  const q80 = (1 - w) * TIMING_PRIOR_Q80 + w * e80;
  return orderQuantiles(q20, q80);
}

/**
 * Check if a Date is the same local calendar day as "today".
 */
function isUpdatedToday(updatedAt) {
  if (!updatedAt) return false;
  try {
    const d = new Date(updatedAt);
    const today = new Date();
    return (
      d.getFullYear() === today.getFullYear() &&
      d.getMonth() === today.getMonth() &&
      d.getDate() === today.getDate()
    );
  } catch {
    return false;
  }
}

/**
 * Ensure user_language_stats row exists for (id, language_code, on_mobile).
 * Returns current params and thresholds.
 */
async function ensureUserLanguageStatsRow(user_id, language_code, on_mobile) {
  const { data: row } = await supabase
    .from("user_language_stats")
    .select("a,b,q_20,q_80,updated_at")
    .eq("id", user_id)
    .eq("language_code", language_code)
    .eq("on_mobile", !!on_mobile)
    .maybeSingle();

  if (row) {
    return {
      a: Number(row.a) || 0,
      b: Number(row.b) || 0,
      q20: Number(row.q_20),
      q80: Number(row.q_80),
      updated_at: row.updated_at || null,
      existed: true,
    };
  }

  const defaults = on_mobile ? DEFAULT_PARAMS_MOBILE : DEFAULT_PARAMS_DESKTOP;
  const now = new Date().toISOString();
  const insertRow = {
    id: user_id,
    language_code,
    on_mobile: !!on_mobile,
    a: Math.floor(defaults.a),
    b: Math.floor(defaults.b),
    q_20: TIMING_PRIOR_Q20,
    q_80: TIMING_PRIOR_Q80,
    updated_at: now,
  };

  await supabase.from("user_language_stats").upsert(insertRow, {
    onConflict: "id,language_code,on_mobile",
  });

  return {
    a: defaults.a,
    b: defaults.b,
    q20: TIMING_PRIOR_Q20,
    q80: TIMING_PRIOR_Q80,
    updated_at: now,
    existed: false,
  };
}

/**
 * Recalculate q_20 and q_80 using the most recent HISTORY_SAMPLE_LIMIT correct actions.
 * Returns { q20, q80, n } after update (blended with priors if needed).
 */
async function recalcQuantilesForUser(user_id, language_code, on_mobile, a, b) {
  const { data: actions, error } = await supabase
    .from("card_actions")
    .select(
      "duration_recall_ms,duration_typing_ms,correct_answer_character_length,language,on_mobile"
    )
    .eq("language", language_code)
    .eq("on_mobile", !!on_mobile)
    .eq("correct", true)
    .order("date", { ascending: false })
    .limit(HISTORY_SAMPLE_LIMIT);

  if (error) {
    console.error("Failed to load card_actions for quantiles:", error);
    // Fallback to priors
    return { q20: TIMING_PRIOR_Q20, q80: TIMING_PRIOR_Q80, n: 0 };
  }

  const arr = [];
  const A = Number(a) || 0;
  const B = Number(b) || 0;

  for (const r of actions || []) {
    const dr = Number(r?.duration_recall_ms);
    const dt = Number(r?.duration_typing_ms);
    const L = Math.max(1, Number(r?.correct_answer_character_length) || 0);
    if (!Number.isFinite(dr) || !Number.isFinite(dt)) continue;
    const expected = A + B * L;
    if (!Number.isFinite(expected) || expected <= 0) continue;
    const tnorm = (dr + dt) / expected;
    if (Number.isFinite(tnorm) && tnorm > 0) arr.push(tnorm);
  }

  const n = arr.length;
  if (n === 0) {
    return { q20: TIMING_PRIOR_Q20, q80: TIMING_PRIOR_Q80, n: 0 };
  }

  const emp20 = percentileNearestRank(arr, 0.2);
  const emp80 = percentileNearestRank(arr, 0.8);
  const { q20, q80 } = blendedQuantiles(emp20, emp80, n);

  const now = new Date().toISOString();
  await supabase
    .from("user_language_stats")
    .update({ q_20: q20, q_80: q80, updated_at: now })
    .eq("id", user_id)
    .eq("language_code", language_code)
    .eq("on_mobile", !!on_mobile);

  return { q20, q80, n };
}

/**
 * Compute expected time and t_norm for a single correct response.
 */
function computeTNorm(a, b, recallMs, typingMs, answerLen) {
  const A = Number(a) || 0;
  const B = Number(b) || 0;
  const dr = Number(recallMs);
  const dt = Number(typingMs);
  const L = Math.max(1, Number(answerLen) || 0);
  if (!Number.isFinite(dr) || !Number.isFinite(dt))
    return { expected: NaN, t_norm: NaN };
  const expected = A + B * L;
  if (!Number.isFinite(expected) || expected <= 0)
    return { expected: NaN, t_norm: NaN };
  return { expected, t_norm: (dr + dt) / expected };
}

/**
 * Classify a correct response by timing:
 * - Easy if t_norm <= q_20
 * - Hard if t_norm >= q_80
 * - Good otherwise
 */
function classifyByQuantiles(t_norm, q20, q80) {
  const { q20: low, q80: high } = orderQuantiles(q20, q80);
  if (!Number.isFinite(t_norm)) return "Good";
  if (t_norm <= low) return "Easy";
  if (t_norm >= high) return "Hard";
  return "Good";
}

/**
 * External API for LearnView/consumers:
 * Decide FSRS rating for a response based on timing and user percentiles.
 *
 * Incorrect -> "Again"
 * Correct   -> "Easy" | "Good" | "Hard"
 */
export async function getTimingBasedFsrsRating({
  user_id,
  language_code,
  on_mobile,
  correct,
  duration_recall_ms,
  duration_typing_ms,
  correct_answer_character_length,
}) {
  // Incorrect always "Again"
  if (!correct) {
    return {
      rating: "Again",
      used_timing: false,
      t_norm: null,
      q20: null,
      q80: null,
    };
  }

  // 1) Load or create stats row
  const stats = await ensureUserLanguageStatsRow(
    user_id,
    language_code,
    on_mobile
  );

  // 2) If not updated today, recompute percentiles using latest a/b
  let q20 = stats.q20;
  let q80 = stats.q80;
  if (!isUpdatedToday(stats.updated_at)) {
    const recalc = await recalcQuantilesForUser(
      user_id,
      language_code,
      on_mobile,
      stats.a,
      stats.b
    );
    q20 = recalc.q20;
    q80 = recalc.q80;
  }

  // 3) Compute current t_norm and classify
  const { t_norm } = computeTNorm(
    stats.a,
    stats.b,
    duration_recall_ms,
    duration_typing_ms,
    correct_answer_character_length
  );
  const rating = classifyByQuantiles(t_norm, q20, q80);

  return {
    rating, // "Easy" | "Good" | "Hard"
    used_timing: Number.isFinite(t_norm),
    t_norm: Number.isFinite(t_norm) ? t_norm : null,
    q20,
    q80,
  };
}
